import React, { type FC, useMemo } from 'react';
import { Inbox } from 'lucide-react';
import List from '@mui/material/List';
import NavigationLink from 'src/components/layout/landlord/NavigationLink';
import { useAllInboxes, DEFAULT_INBOX_ID, getInboxUrl } from 'src/services/api/inboxes/use-all-inboxes';
import { usePrimaryInbox } from 'src/services/api/inboxes/use-primary-inbox';

import styles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';

type Props = {
    onNavigationChange: () => void;
    isSidebarCollapsed: boolean;
}

export const DesktopNavigationInboxesLink: FC<Props> = ({ onNavigationChange, isSidebarCollapsed }) => {
    const { data: allInboxes } = useAllInboxes();
    const { primaryInboxId } = usePrimaryInbox();

    const inboxLinks = useMemo(() => {
        if (!allInboxes) {
            return { virtualTeam: [], virtualPrivate: [], additional: [] };
        }

        const primaryInbox = primaryInboxId !== DEFAULT_INBOX_ID ?
            allInboxes.find(inbox => inbox.id === primaryInboxId) :
            undefined;

        const virtualTeam = allInboxes
            .filter(inbox => inbox.is_virtual && !inbox.is_private)
            .map(inbox => (
                <NavigationLink
                    key={inbox.id}
                    text={inbox.name}
                    link={getInboxUrl(inbox)}
                    onClick={onNavigationChange}
                    testId={`link-inboxes-${inbox.id}`}
                />
            ));

        const virtualPrivate = allInboxes
            .filter(inbox => inbox.is_virtual && inbox.is_private)
            .map(inbox => (
                <NavigationLink
                    key={inbox.id}
                    text={inbox.name}
                    link={getInboxUrl(inbox)}
                    onClick={onNavigationChange}
                    testId={`link-inboxes-${inbox.id}`}
                />
            ));

        const additional = [
            primaryInbox ? (
                <NavigationLink
                    key={primaryInbox.id}
                    text={primaryInbox.name}
                    link={getInboxUrl(primaryInbox)}
                    onClick={onNavigationChange}
                    testId={`link-inboxes-${primaryInbox.id}`}
                />
            ) : null,
            ...allInboxes
                .filter(inbox => !inbox.is_virtual && inbox.is_favorite && inbox.id !== primaryInbox?.id)
                .map(inbox => (
                    <NavigationLink
                        key={inbox.id}
                        text={inbox.name}
                        link={getInboxUrl(inbox)}
                        onClick={onNavigationChange}
                        testId={`link-inboxes-${inbox.id}`}
                    />
                )),
        ].filter(Boolean);

        return { virtualTeam, virtualPrivate, additional };
    }, [allInboxes, primaryInboxId, onNavigationChange]);

    return (
        <>
            <NavigationLink
                text={isSidebarCollapsed ? '' : 'Inboxes'}
                icon={<Inbox />}
                link="/landlord/inner/inboxes"
                onClick={onNavigationChange}
            />
            {!isSidebarCollapsed && (
                <List className={styles.inboxLinks}>
                    {inboxLinks.virtualTeam}
                    {inboxLinks.virtualPrivate}
                    {inboxLinks.additional}
                </List>
            )}
        </>
    );
};
